import { useAuth0 } from "@auth0/auth0-react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SportsFootballRoundedIcon from "@mui/icons-material/SportsFootballRounded";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

const features = [
  {
    icon: <SportsFootballRoundedIcon />,
    title: "Built for college football",
    copy: "Pick every conference, follow live results, and keep each league on the rules that make it yours.",
  },
  {
    icon: <GroupsRoundedIcon />,
    title: "Made for your people",
    copy: "Create a private league, share one code, and turn every Saturday into a season-long rivalry.",
  },
];

const Home = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const signIn = () =>
    loginWithRedirect({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: "openid profile email offline_access",
      },
      appState: { returnTo: "/dashboard" },
    });

  return (
    <HelmetProvider>
      <Helmet defer={false}>
        <title>Pickem - College Football Pickem</title>
      </Helmet>
      <Box
        sx={{
          width: "100%",
          maxWidth: 1320,
          mx: "auto",
          px: { xs: 2, md: "60px" },
          py: { xs: 4, md: 8 },
        }}
      >
        <Paper
          component="section"
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            minHeight: { xs: 420, md: 500 },
            p: { xs: 3, md: 7 },
            display: "flex",
            alignItems: "center",
            border: 1,
            borderColor: "divider",
            bgcolor: "surface.container",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              width: 520,
              height: 520,
              right: -160,
              top: -180,
              borderRadius: "50%",
              bgcolor: "primary.main",
              opacity: 0.12,
            }}
          />
          <Stack spacing={3} sx={{ position: "relative", maxWidth: 680 }}>
            <Typography variant="overline" color="primary.main">
              College football, picked together
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "4.5rem" },
                lineHeight: { xs: 1.08, md: 1.02 },
                maxWidth: 720,
              }}
            >
              Make the call before kickoff.
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 590, fontWeight: 400 }}>
              Create private Pickem leagues, track every rivalry, and see how your Saturday instincts stack up.
            </Typography>
            <Box>
              <Button
                variant="contained"
                size="large"
                onClick={isAuthenticated ? () => navigate("/dashboard") : signIn}
                endIcon={<ArrowForwardRoundedIcon />}
              >
                {isAuthenticated ? "Go to Dashboard" : "Sign in to play"}
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          {features.map((feature) => (
            <Grid item xs={12} md={6} key={feature.title}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box sx={{ color: "primary.main", mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h4" component="h2" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary">{feature.copy}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </HelmetProvider>
  );
};

export default Home;
